import { useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";

import FloorplanImages from "./FloorplanImages";
import FloorplanEditor from "./FloorplanEditor";
import InlineEdit from "./InlineEdit";
import TextInput from "./refresh/inputs/TextInput";
import Label from "./refresh/Label";
import { initFloorplanOptions } from "../screens/Floorplans";
import EditMediaHotspot from "./hotspots/types/EditMediaHotspot_forwardRef";
import { v4 as uuid } from "uuid";
import FloorplanMedias from "./FloorplanMedias";
import HotspotDeckPreview from "./HotspotDeckPreview";

const Floorplan = ({ floorplan, images, tour, onUpdateTour }) => {
  const mediaHotspotRef = useRef(null);
  const enable3D = !!tour.data.options?.floorplans?.enable3D;
  const [floorplanHeight, setFloorplanHeight] = useState(0);
  const [showHotspotPageModal, setShowHotspotPageModal] = useState(false);

  const classes = useStyles({
    active: enable3D,
  });

  const onUpdateFloorplan = (floorplan = {}) => {
    const floorplans = [...tour.data.floorplans].map(item => {
      if (item.uuid !== floorplan.uuid) {
        return item;
      }
      return floorplan;
    });

    onUpdateTour(
      produce(initFloorplanOptions(tour), draft => {
        draft.data.floorplans = floorplans;
        draft.data.options.floorplans.stacks[0].floors = [
          ...draft.data.floorplans,
        ]
          .reverse()
          .map(({ uuid, name }) => ({
            uuid,
            name,
            scale: 1.5,
            model: { enabled: false },
          }));
      }),
    );
  };

  const toggle3D = () => {
    onUpdateTour(
      produce(initFloorplanOptions(tour), draft => {
        draft.data.options.floorplans.enable3D = !enable3D;
        draft.data.options.floorplans.stacks[0].floors = [
          ...draft.data.floorplans,
        ]
          .reverse()
          .map(({ uuid, name }) => ({
            uuid,
            name,
            scale: 1.5,
            model: { enabled: false },
          }));
      }),
    );
  };

  const addMediaHotspot = () => {
    mediaHotspotRef.current?.show({
      uuid: uuid(),
      u: 0,
      v: 0,
      ratio: 0,
      options: {},
      tooltip: "",
    });
  }

  const editMediaHotspot = (hotspot) => {
    mediaHotspotRef.current?.show({
      ...hotspot,
      uuid: hotspot.id,
    });
  }

  const onSaveMediaHotspot = (config) => {
    let _config = { ...config };
    if (!_config.id) _config.id = uuid();
    mediaHotspotRef.current?.hide();
    onUpdateTour(
      produce(initFloorplanOptions(tour), draft => {
        const { floorplans } = draft.data.options;
        const medias = floorplans.medias ? [...floorplans.medias] : [];
        if (medias.some(i => i.id === _config.id)) {
          const idx = medias.findIndex(i => i.id === _config.id);
          medias[idx] = _config;
        } else {
          medias.push(_config);
        }
        draft.data.options.floorplans.medias = medias;
      }),
    );
  }

  const onHotspotPositionChange = (hotspotUuid, offsetPosition) => {
    const updatedFloorplan = {
      ...floorplan,
      hotspots: floorplan.hotspots.map(hotspot => {
        if (hotspot.uuid === hotspotUuid) {
          return {
            ...hotspot,
            offsetX: offsetPosition.offsetX,
            offsetY: offsetPosition.offsetY,
          };
        }
        return hotspot;
      })
    };
    
    onUpdateFloorplan(updatedFloorplan);
  };

  const onDeleteMedia = (mediaId) => {
    onUpdateTour(
      produce(initFloorplanOptions(tour), draft => {
        draft.data.options.floorplans.medias = draft.data.options.floorplans.medias.filter(i => i.id !== mediaId);
        const floorplans = [...tour.data.floorplans].map(item => {
          if (item.uuid !== floorplan.uuid) {
            return item;
          }
          return {
            ...floorplan,
            hotspots: item.hotspots.filter(i => i.image !== mediaId)
          };
        });
        draft.data.floorplans = floorplans;
      }),
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <Label name="3D floorplans">
          <button className={classes.button} onClick={toggle3D}>
            {enable3D ? "Disable" : "Enable"}
          </button>
        </Label>
        <Label name="Add media hotspot">
          <button className={classes.buttonInactive} onClick={addMediaHotspot}>
            Add
          </button>
        </Label>
        {enable3D && (
          <div className={classes.startingLevel}>
            <TextInput
              label="Starting level"
              type="number"
              value={`${(tour.data.options.floorplans.stacks[0]?.start ?? 0) + 1}`}
              onChange={value =>
                onUpdateTour(
                  produce(tour, draft => {
                    const num = parseInt(value, 10) - 1;
                    draft.data.options.floorplans.stacks[0].start =
                      num >= 0 && num < draft.data.floorplans.length ? num : 0;
                  }),
                )
              }
            />
            <span className={classes.startingLevelNote}>
              Level 1 is the bottom of the stack; higher numbers go up.
            </span>
          </div>
        )}
        <Label name="Floor name">
          <InlineEdit
            tagName="span"
            value={floorplan.name}
            className={classes.name}
            onChange={name => onUpdateFloorplan({ ...floorplan, name })}
          />
        </Label>
      </div>
      <div className={classes.mapPane}>
        <div className={classes.editorWrap}>
          <FloorplanEditor
            images={images}
            medias={tour.data.options?.floorplans?.medias || []}
            tour={tour}
            floorplan={floorplan}
            onUpdateFloorplan={onUpdateFloorplan}
            setFloorplanHeight={setFloorplanHeight}
          />
        </div>
        <div className={classes.hotspotPageButton}>
          <button
            className={classes.button}
            onClick={() => setShowHotspotPageModal(true)}
          >
            Adjust Position on Hotspot Page
          </button>
        </div>
      </div>
      <div className={classes.scenes}>
        <FloorplanMedias
          medias={tour.data.options?.floorplans?.medias || []}
          height={floorplanHeight}
          onDelete={onDeleteMedia}
          onSelect={editMediaHotspot}
          used={[]}
        />
        <FloorplanImages
          images={images}
          height={floorplanHeight}
          used={floorplan.hotspots.map(x => x.image)}
        />
      </div>
      <EditMediaHotspot
        ref={mediaHotspotRef}
        account={tour.account}
        onComplete={onSaveMediaHotspot}
      />
      <HotspotDeckPreview 
        show={showHotspotPageModal}
        onClose={() => setShowHotspotPageModal(false)}
        backgroundImage={tour?.data?.options?.posterPage?.image?.slice(5, -2)}
        floorplan={floorplan}
        tour={tour}
        images={images}
        activeDeck={floorplan?.id || floorplan?.uuid}
        onHotspotPositionChange={onHotspotPositionChange}
      />
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  container: {
    color: "white",
    flex: 1,
    "min-height": 0,
    display: "flex",
    "flex-direction": "column",
    overflow: "hidden",
  },
  mapPane: {
    flex: 1,
    "min-height": 0,
    display: "flex",
    "flex-direction": "column",
    overflow: "hidden",
  },
  editorWrap: {
    flex: 1,
    "min-height": 0,
    overflow: "hidden",
    "& > *": {
      height: "100%",
    },
  },
  hotspotPageButton: {
    paddingLeft: 24,
    display: "flex",
    "justify-content": "flex-start",
    "flex-shrink": 0,
  },
  scenes: {
    flex: "0 0 auto",
    "min-height": 160,
    "max-height": "32vh",
    "overflow-y": "auto",
    "border-top": "1px solid #575757",
  },
  top: {
    width: "100%",
    padding: 10,
    display: "flex",
    "flex-direction": "row",
    "border-bottom": "1px solid #575757",

    "& label": {
      margin: [0, 0, 0, 15],
    },
  },
  startingLevel: {
    display: "flex",
    "flex-direction": "column",
    "max-width": 220,
  },
  startingLevelNote: {
    color: "#9F9F9F",
    "font-size": 11,
    "line-height": "1.3",
    "margin-left": 15,
    "margin-top": 4,
  },
  button: {
    background: props => (props.active ? "#f90" : "#575757"),
    color: "white",
    outline: "none",
    border: "none",
    "border-radius": 3,
    "margin-top": 3,
    cursor: "pointer",
    height: 29,
  },
  buttonInactive: {
    background: "#575757",
    color: "white",
    outline: "none",
    border: "none",
    "border-radius": 3,
    "margin-top": 3,
    cursor: "pointer",
    height: 29,
  },
  name: {
    fontSize: 20,
  },
}));

export default Floorplan;
